import React from 'react';
import { Badge } from 'reactstrap';
import { parseHtml, stripHtml } from 'helpers/index';
import { ThunderboltOutlined } from '@ant-design/icons';
import {
  SectionBlock,
  StyledCollapse,
  BorderlessPanel,
  MicroHeaderRow,
  MicroIconBadge,
  MicroTitle,
  AiPill,
  MicroBody,
} from './AdaptiveStyles';
import {
  QUESTION_TYPE,
  RESULT_STATUS,
  UNIT_STATUS,
  USER_ROLES,
} from 'constants/constants';
import {
  DragAndDrop,
  FillInTheBlank,
  FillTheGap,
  FreeText,
  MultipleChoice,
  Ordering,
} from 'modules/Course/Unit/components/QuestionTypev2';
import { useUnit } from 'hooks/units';
import { useViewMode } from 'hooks';
import { formatHHmmDDMMYYYY } from 'helpers/format-time';
import { Alert } from 'reactstrap';
import stylesCourse from 'styles/Course.module.scss';
import DragDropResultPage from 'modules/Course/Unit/components/ContentTest/ContentResult/ResultItem/DragDropPageResult';
import OrderingResult from 'modules/Course/Unit/components/ContentTest/ContentResult/ResultItem/OrderingResult';
import MTPchoiceResult from 'modules/Course/Unit/components/ContentTest/ContentResult/ResultItem/MTPchoiceResult';

/** Same palette family as `ModalDetail.js` (Report/Tests). */
export const ADAPTIVE_COLOR_STATUS = {
  PENDING: 'warning',
  COMPLETED: 'success',
  FAILED: 'danger',
  PASSED: 'success',
  NOT_ATTEMPT: 'secondary',
};

const ADAPTIVE_RESULT_UI = {
  pageBg: 'linear-gradient(135deg, #f5f3ff 0%, #ffffff 50%, #eff6ff 100%)',
  cardShadow: '0 10px 40px rgba(17, 24, 39, 0.08)',
  cardBorder: '1px solid rgba(229, 231, 235, 0.8)',
  cardRadius: 20,
  innerMaxWidth: 860,
  track: '#eef2f7',
  mutedText: '#6b7280',
  titleText: '#1f2937',
  successBg: 'rgba(34, 197, 94, 0.12)',
  successText: '#16a34a',
  dangerBg: 'rgba(239, 68, 68, 0.12)',
  dangerText: '#dc2626',
  warningBg: 'rgba(245, 158, 11, 0.12)',
  warningText: '#d97706',
  mutedBg: 'rgba(107, 114, 128, 0.10)',
};

function ResultStatCard({ label, value, tone = 'default' }) {
  const toneMap = {
    default: { bg: '#ffffff', color: ADAPTIVE_RESULT_UI.titleText },
    success: {
      bg: ADAPTIVE_RESULT_UI.successBg,
      color: ADAPTIVE_RESULT_UI.successText,
    },
    danger: {
      bg: ADAPTIVE_RESULT_UI.dangerBg,
      color: ADAPTIVE_RESULT_UI.dangerText,
    },
    warning: {
      bg: ADAPTIVE_RESULT_UI.warningBg,
      color: ADAPTIVE_RESULT_UI.warningText,
    },
    muted: {
      bg: ADAPTIVE_RESULT_UI.mutedBg,
      color: ADAPTIVE_RESULT_UI.mutedText,
    },
  };
  const t = toneMap[tone] || toneMap.default;
  return (
    <div
      style={{
        flex: '1 1 140px',
        minWidth: 120,
        background: t.bg,
        border: ADAPTIVE_RESULT_UI.cardBorder,
        borderRadius: 16,
        padding: '14px 16px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: t.color,
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 12,
          color: ADAPTIVE_RESULT_UI.mutedText,
          marginTop: 4,
          fontWeight: 500,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function QuestionStatusIcon({ status }) {
  if (status === RESULT_STATUS.COMPLETED) {
    return (
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: ADAPTIVE_RESULT_UI.successBg,
          color: ADAPTIVE_RESULT_UI.successText,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 18,
          flexShrink: 0,
        }}
        aria-label="Correct"
      >
        ✓
      </div>
    );
  }
  if (status === RESULT_STATUS.FAILED) {
    return (
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: ADAPTIVE_RESULT_UI.dangerBg,
          color: ADAPTIVE_RESULT_UI.dangerText,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 18,
          flexShrink: 0,
        }}
        aria-label="Incorrect"
      >
        ✕
      </div>
    );
  }
  if (status === RESULT_STATUS.PENDING) {
    return (
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: ADAPTIVE_RESULT_UI.warningBg,
          color: ADAPTIVE_RESULT_UI.warningText,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 16,
          flexShrink: 0,
        }}
        aria-label="Pending"
      >
        …
      </div>
    );
  }
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: ADAPTIVE_RESULT_UI.mutedBg,
        color: ADAPTIVE_RESULT_UI.mutedText,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 16,
        flexShrink: 0,
      }}
      aria-label="Not answered"
    >
      —
    </div>
  );
}

/**
 * Normalize adaptive API payloads so report result components receive expected shapes.
 * @param {Record<string, any>} item
 */
export function normalizeAdaptiveResult(item) {
  if (!item) return item;
  const next = { ...item };

  if (item.type === QUESTION_TYPE.DRAGDROP) {
    const hasAnswers =
      Array.isArray(next.resultAnswers) && next.resultAnswers.length > 0;
    const raw = next.result;
    if (!hasAnswers && Array.isArray(raw) && raw.length > 0) {
      next.resultAnswers = raw.map((content, i) => ({
        content: typeof content === 'string' ? content : String(content),
        correct:
          Array.isArray(item.resultAnswers) && item.resultAnswers[i]
            ? item.resultAnswers[i].correct
            : undefined,
      }));
    }
  }

  if (
    (item.type === QUESTION_TYPE.FILLTHEGAP ||
      item.type === QUESTION_TYPE.FILLINTHEBLANK) &&
    !next.modifiedContent
  ) {
    let inputIndex = 0;
    next.modifiedContent = (item.contentHTML || '').replace(
      /<select[^>]*>.*?<\/select>|<input[^>]*>/gi,
      (match) => {
        const learnerAnswer =
          next.result && next.result[inputIndex] != null
            ? String(next.result[inputIndex])
            : '';
        inputIndex++;

        if (match.toLowerCase().startsWith('<select')) {
          let newMatch = match;
          if (learnerAnswer) {
            newMatch = match.replace(
              new RegExp(
                `(<option[^>]*value=["']${learnerAnswer.replace(
                  /[.*+?^${}()|[\\]\\\\]/g,
                  '\\\\$&',
                )}["'][^>]*)>`,
                'i',
              ),
              `$1 selected>`,
            );
          }
          return newMatch.replace(/<select/i, '<select disabled');
        } else if (match.toLowerCase().startsWith('<input')) {
          const safeAnswer = learnerAnswer.replace(/"/g, '&quot;');
          if (/value=["']/i.test(match)) {
            return match.replace(
              /value=["'][^"']*["']/i,
              `value="${safeAnswer}" readOnly`,
            );
          }
          return match.replace(/\/?>$/, ` value="${safeAnswer}" readOnly />`);
        }
        return match;
      },
    );
  }

  return next;
}

/**
 * Fill-in / gap: per-blank correct strings from `resultAnswers[].answer` when present.
 * @returns {string[] | null}
 */
function buildFillCorrectResultArray(normalized) {
  const ra = normalized?.resultAnswers;
  if (Array.isArray(ra) && ra.length > 0) {
    const fromAnswers = ra.map((x) =>
      x?.answer != null && x?.answer !== ''
        ? String(x.answer)
        : x?.content != null
        ? String(x.content)
        : '',
    );
    if (fromAnswers.some((s) => s !== '')) return fromAnswers;
  }
  if (Array.isArray(normalized?.correctResult)) {
    return normalized.correctResult.map((x) => String(x ?? ''));
  }
  return null;
}

function getFreetextCorrectText(normalized) {
  const v =
    normalized?.correctDataContent ??
    normalized?.correctAnswer ??
    normalized?.modelAnswer ??
    (Array.isArray(normalized?.resultAnswers) && normalized.resultAnswers[0]
      ? normalized.resultAnswers[0]?.answer ??
        normalized.resultAnswers[0]?.content
      : null);
  if (v == null || String(v).trim() === '') return null;
  return String(v);
}

/**
 * @param {any[]} questions
 * @returns {{ total: number; correct: number; incorrect: number; notAnswered: number; pending: number }}
 */
export function getAttemptAnswerSummary(questions) {
  const list = Array.isArray(questions) ? questions : [];
  let correct = 0;
  let incorrect = 0;
  let notAnswered = 0;
  let pending = 0;
  for (const q of list) {
    const s = q?.status;
    if (s === RESULT_STATUS.COMPLETED) correct += 1;
    else if (s === RESULT_STATUS.FAILED) incorrect += 1;
    else if (s === RESULT_STATUS.NOT_ATTEMPT) notAnswered += 1;
    else if (s === RESULT_STATUS.PENDING) pending += 1;
  }
  return {
    total: list.length,
    correct,
    incorrect,
    notAnswered,
    pending,
  };
}

/** @returns {any[]} */
export function getQuestionsFromAttempt(attempt) {
  if (!attempt) return [];
  if (Array.isArray(attempt.questions) && attempt.questions.length > 0) {
    return attempt.questions;
  }
  if (attempt.content != null || attempt.type != null || attempt._id != null) {
    return [attempt];
  }
  return [];
}

export function renderQuestionStem(item, index) {
  const orderLabel = item?.order != null ? `${item.order})` : `${index + 1})`;

  if (
    item?.type === QUESTION_TYPE.FREETEXT ||
    item?.type === QUESTION_TYPE.MTC ||
    item?.type === QUESTION_TYPE.DRAGDROP ||
    item?.type === QUESTION_TYPE.ORDERING
  ) {
    return (
      <div className="d-flex align-items-center">
        {orderLabel}
        {parseHtml(
          item?.content,
          'fill-in-the-blank fill-overflow-image manual-customtagp customconten_elecment w-100 ml-50',
        )}
      </div>
    );
  }

  if (item?.type === QUESTION_TYPE.FILLTHEGAP) {
    return (
      <div className="freetext-correct-ans">
        <div className="customconten_elecment d-flex align-items-center fill-overflow-image">
          {orderLabel}{' '}
          {parseHtml(
            item?.contentHTML?.replace(/<select/gi, '<select disabled'),
            'ml-50 w-100 manual-customtagp',
          )}{' '}
        </div>
      </div>
    );
  }

  if (item?.type === QUESTION_TYPE.FILLINTHEBLANK) {
    return (
      <div className="freetext-correct-ans">
        <div className="d-flex align-items-center customconten_elecment">
          {orderLabel}{' '}
          {parseHtml(
            item?.contentHTML?.replace(/<input/gi, '<input disabled readOnly'),
            'fill-in-the-blank ml-50 fill-overflow-image w-100 manual-customtagp',
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center">
      {orderLabel}
      {parseHtml(item?.content, 'fill-overflow-image w-100 ml-50')}
    </div>
  );
}

function answerSection(title, children) {
  return (
    <div className="mt-2">
      <span className="view-page-title">{title}</span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/**
 * Read-only answers — Question Type v2: correct answer + learner answer (non-interactive).
 */
export function AdaptiveAnswerReadOnly({ item }) {
  const normalized = normalizeAdaptiveResult(item);
  const showsCorrectIcon = true;
  const iconsShow = true;

  // For complex types, we render a single unified result component that handles its own headers.
  if (normalized?.type === QUESTION_TYPE.DRAGDROP) {
    return (
      <DragDropResultPage
        isModal={true}
        result={normalized}
        showCorrect={true}
        showYourAnswer={true}
        showCorrectIcon={showsCorrectIcon}
        iconsShow={iconsShow}
        showPlain={true}
      />
    );
  }

  if (normalized?.type === QUESTION_TYPE.ORDERING) {
    return (
      <OrderingResult
        isModal={true}
        result={normalized}
        showCorrect={true}
        showYourAnswer={true}
        showCorrectIcon={showsCorrectIcon}
        iconsShow={iconsShow}
        showPlain={true}
      />
    );
  }

  if (normalized?.type === QUESTION_TYPE.MTC) {
    return (
      <MTPchoiceResult
        result={normalized}
        showCorrect={true}
        showYourAnswer={true}
        showCorrectIcon={showsCorrectIcon}
        iconsShow={iconsShow}
        showPlain={true}
      />
    );
  }

  // For simple types (FreeText, Fill-in-the-blank), we keep the separated Correct/Learner headers.
  const renderCorrectBlock = () => {
    if (normalized?.type === QUESTION_TYPE.FREETEXT) {
      const text = getFreetextCorrectText(normalized);
      if (text == null) return <span className="text-muted">—</span>;
      return (
        <div
          className={`${stylesCourse['item-section-normal']} ${stylesCourse['item-section-enabled']}`}
          style={{ '--labelcolor': iconsShow ? '#19a356' : '#c1c9d4' }}
        >
          {iconsShow && (
            <div
              className="preview_test_correct_lebalgrade"
              style={{ float: 'right' }}
            >
              Correct
            </div>
          )}
          <div className="px-1 py-1">{parseHtml(text)}</div>
        </div>
      );
    }

    if (
      normalized?.type === QUESTION_TYPE.FILLTHEGAP ||
      normalized?.type === QUESTION_TYPE.FILLINTHEBLANK
    ) {
      const html = normalized?.modifiedContent ?? normalized?.contentHTML ?? '';
      const correctArr = buildFillCorrectResultArray(normalized);
      if (!correctArr || !correctArr.some((s) => String(s).trim() !== '')) {
        return <span className="text-muted">—</span>;
      }

      return (
        <div
          className={`${stylesCourse['item-section-normal']} ${stylesCourse['item-section-enabled']}`}
          style={{ '--labelcolor': iconsShow ? '#19a356' : '#c1c9d4' }}
        >
          {iconsShow && (
            <div
              className="preview_test_correct_lebalgrade"
              style={{ float: 'right' }}
            >
              Correct
            </div>
          )}
          <div className="px-1 py-1 fill-bank-span w-100 fill-overflow-image">
            {normalized.type === QUESTION_TYPE.FILLTHEGAP ? (
              parseHtml(normalized?.content ?? html)
            ) : (
              <FillInTheBlank
                contentHTML={item?.contentHTML || item?.content || ''}
                value={correctArr}
                readOnly={true}
              />
            )}
          </div>
        </div>
      );
    }

    return <span className="text-muted">—</span>;
  };

  const renderLearnerBlock = () => {
    if (normalized?.type === QUESTION_TYPE.FREETEXT) {
      const isCorrect = item?.status === RESULT_STATUS.COMPLETED;
      const isIncorrect = item?.status === RESULT_STATUS.FAILED;
      const labelColor = isCorrect
        ? '#19a356'
        : isIncorrect
        ? '#e16969'
        : '#c1c9d4';

      return (
        <div
          className={`${stylesCourse['item-section-normal']} ${stylesCourse['item-section-enabled']}`}
          style={{ '--labelcolor': labelColor }}
        >
          {isCorrect && iconsShow && (
            <div
              className="preview_test_correct_lebalgrade"
              style={{ float: 'right' }}
            >
              Correct
            </div>
          )}
          {isIncorrect && iconsShow && (
            <div
              className="preview_test_wrong_lebalgrade"
              style={{ float: 'right' }}
            >
              Incorrect
            </div>
          )}
          <div className="px-1 py-1">
            {parseHtml(
              Array.isArray(normalized?.result) && normalized.result[0]
                ? String(normalized.result[0])
                : normalized?.dataContent ?? '',
            )}
          </div>
        </div>
      );
    }

    if (
      normalized?.type === QUESTION_TYPE.FILLTHEGAP ||
      normalized?.type === QUESTION_TYPE.FILLINTHEBLANK
    ) {
      const isCorrect = item?.status === RESULT_STATUS.COMPLETED;
      const isIncorrect = item?.status === RESULT_STATUS.FAILED;
      const labelColor = isCorrect
        ? '#19a356'
        : isIncorrect
        ? '#e16969'
        : '#c1c9d4';

      const userAnswers = Array.isArray(item.result) ? item.result : [];

      return (
        <div
          className={`${stylesCourse['item-section-normal']} ${stylesCourse['item-section-enabled']}`}
          style={{ '--labelcolor': labelColor }}
        >
          {isCorrect && iconsShow && (
            <div
              className="preview_test_correct_lebalgrade"
              style={{ float: 'right' }}
            >
              Correct
            </div>
          )}
          {isIncorrect && iconsShow && (
            <div
              className="preview_test_wrong_lebalgrade"
              style={{ float: 'right' }}
            >
              Incorrect
            </div>
          )}
          <div className="px-1 py-1 fill-bank-span w-100 fill-overflow-image">
            {normalized.type === QUESTION_TYPE.FILLTHEGAP ? (
              <FillTheGap
                contentHTML={item?.contentHTML || item?.content || ''}
                value={userAnswers}
                readOnly={true}
              />
            ) : (
              <FillInTheBlank
                contentHTML={item?.contentHTML || item?.content || ''}
                value={userAnswers}
                readOnly={true}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <p className="text-muted small mb-0">
        Unsupported or incomplete question type for this view.
      </p>
    );
  };

  return (
    <div className="adaptive-answer-readonly">
      {normalized?.type !== QUESTION_TYPE.FREETEXT &&
        answerSection('Correct answer:', renderCorrectBlock())}
      {answerSection('Learner answer:', renderLearnerBlock())}
    </div>
  );
}

function statusBadge(item) {
  if (item?.status === RESULT_STATUS.COMPLETED) {
    return <Badge color="success">Correct</Badge>;
  }
  if (item?.status === RESULT_STATUS.FAILED) {
    return <Badge color="danger">Incorrect</Badge>;
  }
  if (item?.status === RESULT_STATUS.NOT_ATTEMPT) {
    return <Badge color="secondary">Not Answered</Badge>;
  }
  if (item?.status === RESULT_STATUS.PENDING) {
    return <Badge color="warning">Pending</Badge>;
  }
  return item?.status ? (
    <Badge color={`light-${ADAPTIVE_COLOR_STATUS[item.status] || 'secondary'}`}>
      {String(item.status)}
    </Badge>
  ) : null;
}

function weightLabel(item) {
  const w = item?.weight;
  const uw = item?.userWeight;
  if (w == null && uw == null) return null;
  const earned =
    item?.status === UNIT_STATUS.FAILED
      ? 0
      : item?.status === UNIT_STATUS.COMPLETED
      ? w
      : uw ?? 0;
  return `${earned}/${w ?? '—'}`;
}

/**
 * Layout mirrors `ModalDetail` question blocks: badge, stem, answer region.
 */
export default function AdaptiveAttemptQuestionDetail({
  attempt,
  user,
  unitId,
}) {
  const { adaptiveSetup, status } = useUnit(unitId);
  // console.log(allen, 'allen');
  console.log(attempt, 'attempt');

  const viewMode = useViewMode();
  const questions = getQuestionsFromAttempt(attempt);
  const summary = getAttemptAnswerSummary(questions);

  const totalQuestionCount = adaptiveSetup?.input?.questionCount;
  const correctCount = questions.filter(
    (item) => item?.status === RESULT_STATUS.COMPLETED,
  ).length;

  const incorrectCount = questions.filter(
    (item) => item?.status === RESULT_STATUS.FAILED,
  ).length;

  const notAttemptCount =
    totalQuestionCount - (correctCount + incorrectCount) || 0;
  console.log(totalQuestionCount, notAttemptCount, 'totalQuestionCount');
  const COLOR_STATUS = {
    PENDING: 'warning',
    COMPLETED: 'success',
    FAILED: 'danger',
    INCOMPLETED: 'danger',
    NOT_PASSED: 'danger',
    RESUBMIT: 'primary',
    RESET: 'primary',
    GRADED: 'success',
    REATTEMPT: 'warning',
  };
  if (!questions.length) {
    return (
      <div
        style={{
          background: ADAPTIVE_RESULT_UI.pageBg,
          borderRadius: ADAPTIVE_RESULT_UI.cardRadius,
          padding: '28px 16px',
        }}
      >
        <div
          style={{
            maxWidth: ADAPTIVE_RESULT_UI.innerMaxWidth,
            margin: '0 auto',
            background: '#ffffff',
            borderRadius: ADAPTIVE_RESULT_UI.cardRadius,
            boxShadow: ADAPTIVE_RESULT_UI.cardShadow,
            border: ADAPTIVE_RESULT_UI.cardBorder,
            padding: 32,
            textAlign: 'center',
            color: ADAPTIVE_RESULT_UI.mutedText,
          }}
        >
          No question data for this attempt.
        </div>
      </div>
    );
  }

  const accuracy = attempt?.accuracy;
  const pointsPct =
    typeof accuracy === 'number'
      ? accuracy.toFixed(0)
      : typeof status?.points === 'number'
      ? status.points.toFixed(0)
      : '0';

  const isPending =
    status === RESULT_STATUS.PENDING && status?.manualMarking?.status;

  let statusLabel = status;
  if (
    status === 'FAILED' ||
    status === 'INCOMPLETED' ||
    status === 'NOT_PASSED' ||
    status === 'NOT PASSED'
  ) {
    statusLabel = 'Incomplete';
  } else if (status === 'COMPLETED') {
    statusLabel = 'Completed';
  } else if (isPending) {
    statusLabel = 'Pending for Review';
  }

  return (
    <div
      className="adaptive-attempt-question-detail"
      style={{
        background: ADAPTIVE_RESULT_UI.pageBg,
        borderRadius: ADAPTIVE_RESULT_UI.cardRadius,
        padding: '28px 16px',
      }}
    >
      <div
        style={{
          maxWidth: ADAPTIVE_RESULT_UI.innerMaxWidth,
          margin: '0 auto',
        }}
      >
        {/* Header / Summary Card */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: ADAPTIVE_RESULT_UI.cardRadius,
            boxShadow: ADAPTIVE_RESULT_UI.cardShadow,
            border: ADAPTIVE_RESULT_UI.cardBorder,
            padding: '24px 28px',
            marginBottom: 20,
          }}
        >
          <div
            className="d-flex flex-wrap align-items-center justify-content-between"
            style={{ gap: 12 }}
          >
            <div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: ADAPTIVE_RESULT_UI.titleText,
                }}
              >
                {user?.fullName ? user?.fullName : '-'}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: ADAPTIVE_RESULT_UI.mutedText,
                  marginTop: 4,
                }}
              >
                Submitted:{' '}
                {formatHHmmDDMMYYYY(attempt?.updatedAt)
                  ? formatHHmmDDMMYYYY(attempt?.updatedAt)
                  : '-'}
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              <Badge
                color={`light-${COLOR_STATUS[status] || 'secondary'}`}
                style={{
                  fontSize: 12,
                  padding: '6px 12px',
                  borderRadius: 999,
                  fontWeight: 600,
                  letterSpacing: 0.3,
                }}
              >
                {statusLabel}
              </Badge>
              {!isPending && (
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 800,
                    color: ADAPTIVE_RESULT_UI.titleText,
                    lineHeight: 1,
                  }}
                >
                  {pointsPct}%
                </div>
              )}
            </div>
          </div>

          {viewMode !== USER_ROLES.LEARNER && (
            <div
              className="d-flex flex-wrap"
              style={{ gap: 12, marginTop: 20 }}
            >
              <ResultStatCard label="Total" value={questions?.length} />
              <ResultStatCard
                label="Correct"
                value={
                  questions.filter(
                    (item) => item?.status === RESULT_STATUS.COMPLETED,
                  ).length
                }
                tone="success"
              />
              <ResultStatCard
                label="Incorrect"
                value={
                  questions.filter(
                    (item) => item?.status === RESULT_STATUS.FAILED,
                  ).length
                }
                tone="danger"
              />
              {/* <ResultStatCard
                label="Re-attempt"
                value={notAttemptCount}
                tone="muted"
              /> */}
            </div>
          )}
        </div>

        {attempt?.aiMicroLearningContent && (
          <SectionBlock style={{ marginBottom: 20 }}>
            <StyledCollapse
              bordered={false}
              defaultActiveKey={[]}
              expandIconPosition="right"
            >
              <BorderlessPanel
                key="micro-learning"
                header={
                  <MicroHeaderRow>
                    <MicroIconBadge>
                      <ThunderboltOutlined />
                    </MicroIconBadge>
                    <MicroTitle>Micro Learning</MicroTitle>
                    <AiPill>AI</AiPill>
                  </MicroHeaderRow>
                }
              >
                <MicroBody>
                  {stripHtml(
                    String(attempt.aiMicroLearningContent || ''),
                  ).trim()}
                </MicroBody>
              </BorderlessPanel>
            </StyledCollapse>
          </SectionBlock>
        )}

        {/* Question Breakdown */}
        <h3
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: ADAPTIVE_RESULT_UI.titleText,
            margin: '4px 4px 12px',
          }}
        >
          Question Breakdown
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {questions.map((item, index) => (
            <div
              key={item._id ?? index}
              className="adaptive-question-block"
              style={{
                background: '#ffffff',
                borderRadius: ADAPTIVE_RESULT_UI.cardRadius,
                boxShadow: ADAPTIVE_RESULT_UI.cardShadow,
                border: ADAPTIVE_RESULT_UI.cardBorder,
                padding: '20px 24px',
              }}
            >
              <div
                className="d-flex align-items-center justify-content-between flex-wrap"
                style={{ gap: 10, marginBottom: 10 }}
              >
                <div className="d-flex align-items-center" style={{ gap: 12 }}>
                  <QuestionStatusIcon status={item?.status} />
                  {statusBadge(item)}
                </div>
                {weightLabel(item) != null && (
                  <p
                    className="mb-0 custom-marktest-weight"
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: ADAPTIVE_RESULT_UI.mutedText,
                      background: ADAPTIVE_RESULT_UI.mutedBg,
                      padding: '4px 12px',
                      borderRadius: 999,
                    }}
                  >
                    {weightLabel(item)}
                  </p>
                )}
              </div>

              {item?.content || item?.contentHTML ? (
                <div className="my-50">
                  <div className="mb-1">
                    <strong
                      className="view-page-title d-flex align-items-center"
                      style={{
                        fontSize: 13,
                        color: ADAPTIVE_RESULT_UI.mutedText,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                      }}
                    >
                      Question
                    </strong>
                  </div>
                  <div
                    style={{
                      fontSize: 16,
                      color: ADAPTIVE_RESULT_UI.titleText,
                      lineHeight: 1.55,
                      pointerEvents: 'none',
                    }}
                  >
                    {renderQuestionStem(item, index)}
                  </div>
                </div>
              ) : null}

              {item?.status !== RESULT_STATUS.NOT_ATTEMPT && (
                <div
                  className="custom_alignment_modal_test"
                  style={{
                    marginTop: 14,
                    paddingTop: 14,
                    borderTop: '1px solid #eef2f7',
                    pointerEvents: 'none',
                  }}
                >
                  <AdaptiveAnswerReadOnly item={item} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
